'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/components/AuthProvider'
import {
  ensureAdSenseOfferwallTag,
  isAdSenseOfferwallAttempt,
  isValidAdSenseOfferwallClient,
  setAdSenseOfferwallEligibility,
  suppressNativeGoogleOfferwall,
} from '@/lib/google-offerwall'
import {
  hasOfferwallAccess,
  subscribeOfferwallAccess,
} from '@/lib/offerwall-access'
import { getOfferwallSettings } from '@/lib/offerwall-settings'
import {
  getArticleAccessMode,
  type PublicSiteConfig,
} from '@/lib/public-site-config'

/** Normal pageviews suppress native messaging. Only a CTA reload may opt in. */
export default function GoogleOfferwallGuard() {
  const pathname = usePathname()
  const { isPremium, isLoading } = useAuth()
  const [settings, setSettings] = useState<PublicSiteConfig | null>(null)
  const [failed, setFailed] = useState(false)
  const [, accessChanged] = useState(0)
  const resolved = useRef(false)
  const attempt = isAdSenseOfferwallAttempt()
  const hasAccess = hasOfferwallAccess()
  const publicPage =
    !!pathname && !/^\/(admin|editor-x|shorts)(\/|$)/.test(pathname)
  const pending =
    attempt &&
    publicPage &&
    !failed &&
    !isPremium &&
    !hasAccess &&
    (isLoading || settings === null)
  const eligible =
    attempt &&
    publicPage &&
    !failed &&
    !isLoading &&
    !isPremium &&
    !hasAccess &&
    settings !== null &&
    getArticleAccessMode(settings) === 'rewarded' &&
    settings.offerwall_provider === 'adsense' &&
    settings.ads_enabled === '1' &&
    isValidAdSenseOfferwallClient(settings.adsense_client)

  useLayoutEffect(() => {
    suppressNativeGoogleOfferwall()
    setAdSenseOfferwallEligibility(pending ? null : eligible)
    if (!pending) resolved.current = true
  }, [pending, eligible, pathname])

  useEffect(() => {
    if (!attempt || !publicPage) return
    let active = true
    const timer = setTimeout(() => {
      if (active && !resolved.current) setFailed(true)
    }, 8_000)
    void getOfferwallSettings(true)
      .then((config) => {
        if (active) setSettings(config)
      })
      .catch(() => {
        if (active) setFailed(true)
      })
    const unsubscribe = subscribeOfferwallAccess(() =>
      accessChanged((value) => value + 1),
    )
    return () => {
      active = false
      clearTimeout(timer)
      unsubscribe()
      setAdSenseOfferwallEligibility(false)
    }
  }, [attempt, publicPage, pathname])

  useEffect(() => {
    if (!eligible || !settings) return
    const controller = new AbortController()
    void ensureAdSenseOfferwallTag(
      settings.adsense_client,
      controller.signal,
    ).then((loaded) => {
      if (!loaded && !controller.signal.aborted) {
        setAdSenseOfferwallEligibility(false)
        setFailed(true)
      }
    })
    return () => controller.abort()
  }, [eligible, settings])

  return null
}
